import { useCallback } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { InboundClient, type InboundTab } from "@/features/warehouse/InboundClient";
import { WarehousePicker, useWarehouseChoice } from "@/features/warehouse/WarehouseParts";
import { inboundKeys, listInbound, type InboundView } from "@/features/warehouse/inbound.api";

// Warehouse → Ontvangsten (docs/design_handoff_wms_mw8/README.md §5): the receipts of a warehouse and,
// behind the switch, the range of our own labels. Reading is wms_inbound; announcing, closing and the
// counter wms_inbound_write — in the screen, and on the API regardless.

interface InboundSearch {
  warehouse?: string;
  tab?: InboundTab;
  view?: InboundView;
  q?: string;
  from?: string;
  to?: string;
  /** De week: a day in the week shown (empty: this week), and the day chosen in it. */
  week?: string;
  day?: string;
}

export const Route = createFileRoute("/_shell/warehouse/inbound/")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "wms_inbound") && !hasModule(context.session, "wms_inbound_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): InboundSearch => {
    const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
    const tab = str(search.tab);
    const view = str(search.view);
    const day = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);
    return {
      warehouse: str(search.warehouse),
      tab: tab === "ownLabels" ? "ownLabels" : undefined,
      view: view === "open" || view === "today" || view === "recent" || view === "all" ? view : undefined,
      q: str(search.q),
      from: day(search.from),
      to: day(search.to),
      week: day(search.week),
      day: day(search.day),
    };
  },
  component: InboundScreen,
});

function InboundScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const canEdit = session.roles.includes("admin") || hasModule(session, "wms_inbound_write");
  const view: InboundView = search.view ?? "week";
  const period = { from: search.from ?? "", to: search.to ?? "" };

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  const choice = useWarehouseChoice(search.warehouse, (code) => updateParams({ warehouse: code, q: "" }));
  const warehouse = choice.current;

  const query = useQuery({
    queryKey: inboundKeys.list(warehouse?.id ?? "", view, search.q ?? "", period, search.week ?? ""),
    queryFn: () => listInbound(warehouse!.id, view, search.q ?? "", period, search.week ?? ""),
    enabled: warehouse !== undefined && search.tab !== "ownLabels",
    placeholderData: keepPreviousData,
  });

  return (
    <div className="wl-page wl-page--list" data-screen-label="Back-office Warehouse Receipts">
      {choice.error ? (
        <div className="wl-alert wl-alert--danger">
          <span>{choice.error instanceof Error ? choice.error.message : t("common.errorGeneric")}</span>
        </div>
      ) : !warehouse ? (
        <p className="t-body-sm t-muted">{choice.isLoading ? t("common.loading") : t("warehouse.noWarehouses")}</p>
      ) : (
        <InboundClient
          warehouse={warehouse}
          picker={<WarehousePicker warehouses={choice.warehouses} current={warehouse} onChoose={choice.choose} />}
          tab={search.tab ?? "receipts"}
          view={view}
          q={search.q ?? ""}
          period={period}
          week={search.week ?? ""}
          day={search.day ?? ""}
          rows={query.data ?? []}
          isLoading={query.isLoading}
          canEdit={canEdit}
          userId={session.userId}
          onTab={(tab) => updateParams({ tab: tab === "receipts" ? "" : tab })}
          onView={(next) => updateParams({ view: next === "week" ? "" : next })}
          onWeek={(week) => updateParams({ week, day: "" })}
          onDay={(day) => updateParams({ day })}
          onSearch={(q) => updateParams({ q })}
          onPeriod={(next) => updateParams({ from: next.from, to: next.to })}
        />
      )}
    </div>
  );
}
